import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { Input } from "@repo/ui/components/Base/Input";
import { Button } from "@repo/ui/components/Base/Button";
import { AuthContext } from "@/providers";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), ""], "Passwords must match")
    .required("Confirm Password is required")
    .label("Confirm Password"),
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
});

export const RegisterForm = () => {
  const [, send] = AuthContext.useActor();
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values) => {
      values = { ...values };
      if (!(await validationSchema.validate(values))) return;

      const success = await new Promise((resolve) =>
        send({ type: "SIGNUP", data: { ...values, next: resolve } })
      );

      if (success) {
        navigate("/auth/verify");
      }
    },
  });

  return (
    <div className="w-full md:w-96">
      <form onSubmit={formik.handleSubmit} className="gap-y-2 w-full">
        <Input
          id="email"
          name="email"
          label="Email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          errorMessage={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : ""
          }
        />
        <Input
          id="firstName"
          name="firstName"
          label="First Name"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          errorMessage={
            formik.touched.firstName && formik.errors.firstName
              ? formik.errors.firstName
              : ""
          }
        />
        <Input
          id="lastName"
          name="lastName"
          label="Last Name"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          errorMessage={
            formik.touched.lastName && formik.errors.lastName
              ? formik.errors.lastName
              : ""
          }
        />
        <Input
          id="password"
          name="password"
          label="Password"
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          errorMessage={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : ""
          }
        />
        <Input
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm Password"
          type="password"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          errorMessage={
            formik.touched.confirmPassword && formik.errors.confirmPassword
              ? formik.errors.confirmPassword
              : ""
          }
        />
        <div className="flex flex-row justify-between mt-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/auth/login")}
            type="button"
            disabled={formik.isSubmitting}
          >
            Back to Login
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Sign Up
          </Button>
        </div>
      </form>
    </div>
  );
};
